import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { LoginFacade } from 'mbs-ui-shared@facades/login.facade';
import { ConfigurationService } from 'mbs-ui-shared@services/configuration.service';
import { EnvironmentService } from 'mbs-ui-shared@services/environments.service';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { isRmmWarehouseRequest } from './helpers/rmm-warehouse-request.helper';

const MANUAL_SESSION_ERROR_HANDLED = 'MANUAL_SESSION_ERROR_HANDLED';

const NO_UNAUTHORIZED_CHECK_LIST = [
  'api/auth/new-token-scope/feedback-access',
  '/api/version',
  'api/auth/new-token-scope/appsbackup-access',
  'api/odata',
  'api/auth/login/refresh'
];

const IGNORE_LIST = ['api/payments/wire-transfer/invoice'];

@Injectable()
export class SessionInterceptor implements HttpInterceptor {
  private injector = inject(Injector);
  private configService = inject(ConfigurationService);
  private environment = inject(EnvironmentService);

  // Lazy getters to break the circular dependency:
  // SessionInterceptor → AuthFacade → AuthService → HttpClient → (withInterceptorsFromDi) → SessionInterceptor
  protected get auth(): AuthFacade {
    return this.injector.get(AuthFacade);
  }

  private get loginFacade(): LoginFacade {
    return this.injector.get(LoginFacade);
  }

  protected isIgnoredRequest(req: HttpRequest<any>) {
    return IGNORE_LIST.some((x) => req.url.includes(x));
  }

  protected isIgnoredRequestError(noUnauthorizedCheckList: string[], req: HttpRequest<any>, error: HttpErrorResponse) {
    return (
      req.headers.has(MANUAL_SESSION_ERROR_HANDLED) || error.status !== 401 || noUnauthorizedCheckList.some((x) => req.url.includes(x))
    );
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isIgnoredRequest(req)) {
      return next.handle(req);
    }

    req = this.addToken(this.auth.requestAuthToken, req);

    const noUnauthorizedCheckList = NO_UNAUTHORIZED_CHECK_LIST.concat(
      this.configService.loaded
        ? ['/commands/invokepscommand', '/commands/invokeactivecommand', '/stat/host/last', '/last/host', '/last/summary', '/hub/negotiate']
        : []
    );

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.isIgnoredRequestError(noUnauthorizedCheckList, req, error)) {
          // rethrow to next interceptor
          return throwError(() => error);
        }

        if (this.auth.isOneTimeSession) {
          return throwError(() => error);
        }

        const refreshAndRetry = this.auth.tryRefreshToken$().pipe(
          switchMap((refreshed) => {
            if (!refreshed) {
              this.logoutUnlessRmmWarehouseRequest(req);

              return throwError(() => error);
            }

            req = this.addToken(this.auth.requestAuthToken, req);
            this.auth.fetchCurrentUser$.subscribe();

            return next.handle(req).pipe(
              catchError((error: HttpErrorResponse) => {
                if (error.status === 401) {
                  this.logoutUnlessRmmWarehouseRequest(req);
                }

                return throwError(() => error);
              })
            );
          })
        );

        if (this.environment.production) {
          // for external service that required auth by JWT
          if (this.environment.useSessionToken) {
            return refreshAndRetry;
          }

          this.logoutUnlessRmmWarehouseRequest(req);

          return throwError(() => error);
        } else {
          return refreshAndRetry;
        }
      })
    );
  }

  private logoutUnlessRmmWarehouseRequest(req: HttpRequest<unknown>): void {
    if (!isRmmWarehouseRequest(req, this.configService)) this.loginFacade.logout();
  }

  private addToken(tokenData: string, req: HttpRequest<any>): HttpRequest<any> {
    const customHeaders = ['CustomAuth'];

    if (tokenData && !req.headers.get('CustomAuth')) {
      req = req.clone({ headers: req.headers.set('Authorization', tokenData) });
    }

    customHeaders.forEach((header) => {
      req = req.clone({ headers: req.headers.delete(header) });
    });

    return req;
  }
}
